<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import Stats from 'stats.js'
import * as THREE from 'three'
import {
  GSplatBoxShaderEffect,
  GSplatCropShaderEffect,
  GSplatObbShaderEffect,
  GSplatRelighting,
  type GSplatRelightingViewController,
  type GSplatRelightingViewFrame,
  GSplatRevealGridEruption,
  GSplatRevealRadial,
  GSplatRevealRain,
  GSplatTrees,
  type GSplatTreesAttachment,
  type GSplatTreeSphere,
  type GSplatWeatherController,
  type GaussianSplatPlugin,
  presets,
  type GSplatShaderEffectLike,
} from '@sharefe/share3d-engine'
import { getDefaultPresetModelUrl, PRESET_MODELS } from '../examples/presetModels'
import { use3DGS } from '../hooks/use3DGS'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import GSplatBackendControlCard from './GSplatBackendControlCard.vue'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import SceneControlPanel from './SceneControlPanel.vue'
import SceneGroupCard from './SceneGroupCard.vue'
import { createCameraAdaptiveObb, type CameraAdaptiveObbParameters } from '../utils/gsplatAdaptiveObb'
import {
  applyGs3dEffectsInitialCamera,
  GS3D_EFFECTS_CAMERA_FOV,
} from '../utils/gs3dEffectsCamera'
import {
  configureGsplatPlugins,
  getGsplatGraphicsConfig,
  readGsplatBackendControlState,
  type GsplatBackendControlState,
} from '../utils/gsplatBackendControl'

type ShaderEffectOption = {
  key: string
  label: string
  detail: string
  create: (context: EffectRuntimeContext) => GSplatShaderEffectLike
}

type EffectRuntimeContext = {
  center: THREE.Vector3
  boxMin: [number, number, number]
  boxMax: [number, number, number]
  boxSweepMin: [number, number, number]
  boxSweepMax: [number, number, number]
  adaptiveObb: CameraAdaptiveObbParameters
  sceneRadius: number
}

type Vector3Tuple = [number, number, number]
type ColorTuple = [number, number, number]
type BoxSweepDirectionKey = 'z-up' | 'z-down' | 'x-plus' | 'x-minus' | 'y-plus' | 'y-minus'
type RevealPanelKey = 'radial' | 'rain' | 'grid'
type AdvancedEffectKey = 'relighting' | 'trees' | 'weather'

interface GS3DEffectsDiagnostics {
  readonly backend: string | null
  readonly requestedSortingMode: 'auto' | 'cpu' | 'gpu'
  readonly requestedGpuRadixKind: 'auto' | 'portable' | 'onesweep' | null
  readonly gpuPickIdMode: 'direct' | 'buffer'
  readonly active: AdvancedEffectKey | null
  readonly status: string
  readonly relighting:
    | {
        readonly enabled: boolean
        readonly initialized: boolean
        readonly revision: number
        readonly targetSize: { readonly width: number; readonly height: number } | null
      }
    | null
  readonly trees:
    | {
        readonly enabled: boolean
        readonly revision: number
        readonly sphereCount: number
        readonly hasAttachment: boolean
        readonly needsContinuousActivity: boolean
      }
    | null
  readonly weather:
    | {
        readonly hasHandle: boolean
        readonly planCount: number
        readonly time: number
        readonly needsContinuousActivity: boolean
        readonly lastError: string | null
      }
    | null
}

declare global {
  interface Window {
    __SHARE3D_GS3D_EFFECT_DIAGNOSTICS__?: GS3DEffectsDiagnostics
  }
}

type RevealNumberField = {
  key: string
  label: string
  min: number
  max: number
  step: number
}

type RevealColorField = {
  key: string
  label: string
}

type RevealColorControl = {
  hex: string
  intensity: number
}

// 默认加载预设 model1
const DEFAULT_GS3D_MODEL = getDefaultPresetModelUrl()
const DEFAULT_BOX_SIZE = 4
const DEFAULT_SCENE_RADIUS = 32
const MIN_REVEAL_RADIUS = 10
const DEFAULT_BOX_DURATION = 3.5
const BOX_EDGE_TINT_INTENSITY = 5
const PLAYCANVAS_REVEAL_RADIAL_SPEED = 5
const PLAYCANVAS_REVEAL_RADIAL_DELAY = 3
const PLAYCANVAS_REVEAL_RAIN_DISTANCE = 30
const PLAYCANVAS_REVEAL_RAIN_SPEED = 3
const PLAYCANVAS_REVEAL_GRID_DELAY = 0.2
const COLOR_INTENSITY_MAX = 5
const COLOR_INTENSITY_STEP = 0.05
const DEFAULT_COLOR_INTENSITY = 1
const BOX_SWEEP_DIRECTIONS: Array<{
  key: BoxSweepDirectionKey
  label: string
  vector: Vector3Tuple
}> = [
    { key: 'z-up', label: '上', vector: [0, 0, 1] },
    { key: 'z-down', label: '下', vector: [0, 0, -1] },
    { key: 'x-plus', label: 'X+', vector: [1, 0, 0] },
    { key: 'x-minus', label: 'X-', vector: [-1, 0, 0] },
    { key: 'y-plus', label: 'Y+', vector: [0, 1, 0] },
    { key: 'y-minus', label: 'Y-', vector: [0, -1, 0] },
  ]
const BOX_COLOR_PRESETS = [
  { label: '青', value: '#00ffff' },
  { label: '黄', value: '#ffff00' },
  { label: '橙', value: '#ff8a24' },
  { label: '蓝', value: '#4aa3ff' },
  { label: '粉', value: '#ff6ad5' },
]

const boxSize = ref(DEFAULT_BOX_SIZE)
const boxDirectionKey = ref<BoxSweepDirectionKey>('z-up')
const boxDurationSeconds = ref(DEFAULT_BOX_DURATION)
const boxTintColor = ref('#00ffff')
const boxEdgeColor = ref('#ffff00')
const radialRevealParams = ref({
  speed: PLAYCANVAS_REVEAL_RADIAL_SPEED,
  acceleration: 0,
  delay: PLAYCANVAS_REVEAL_RADIAL_DELAY,
  dotTint: [0, 1, 1] as ColorTuple,
  waveTint: [1, 0.5, 0] as ColorTuple,
  oscillationIntensity: 0.2,
  bandWidth: 1,
})
const obbRevealParams = ref({
  speed: PLAYCANVAS_REVEAL_RADIAL_SPEED,
  acceleration: 0,
  delay: PLAYCANVAS_REVEAL_RADIAL_DELAY,
  dotTint: [0, 1, 1] as ColorTuple,
  waveTint: [1, 0.5, 0] as ColorTuple,
  oscillationIntensity: 0.2,
  bandWidth: 1,
})
const rainRevealParams = ref({
  distance: PLAYCANVAS_REVEAL_RAIN_DISTANCE,
  speed: PLAYCANVAS_REVEAL_RAIN_SPEED,
  acceleration: 0,
  flightTime: 2,
  rainSize: 0.015,
  rotation: 0.9,
  fallTint: [0, 1, 1] as ColorTuple,
  fallTintIntensity: 0.2,
  hitTint: [2, 0, 0] as ColorTuple,
  hitDuration: 0.5,
})
const gridRevealParams = ref({
  blockSize: 2,
  delay: PLAYCANVAS_REVEAL_GRID_DELAY,
  duration: 1,
  dotSize: 0.01,
  moveTint: [1, 0, 1] as ColorTuple,
  moveTintIntensity: 0.2,
  landTint: [2, 2, 0] as ColorTuple,
  landDuration: 0.6,
})
const revealColorControls = ref<Record<RevealPanelKey, Record<string, RevealColorControl>>>({
  radial: {
    dotTint: createColorControl(radialRevealParams.value.dotTint),
    waveTint: createColorControl(radialRevealParams.value.waveTint),
  },
  rain: {
    fallTint: createColorControl(rainRevealParams.value.fallTint),
    hitTint: createColorControl(rainRevealParams.value.hitTint),
  },
  grid: {
    moveTint: createColorControl(gridRevealParams.value.moveTint),
    landTint: createColorControl(gridRevealParams.value.landTint),
  },
})
const obbRevealColorControls = ref<Record<string, RevealColorControl>>({
  dotTint: createColorControl(obbRevealParams.value.dotTint),
  waveTint: createColorControl(obbRevealParams.value.waveTint),
})
const selectedBoxDirection = computed(() => {
  return BOX_SWEEP_DIRECTIONS.find((direction) => direction.key === boxDirectionKey.value) ?? BOX_SWEEP_DIRECTIONS[0]
})
const normalizedBoxSize = computed(() => {
  const size = Number(boxSize.value)
  return Number.isFinite(size) ? size : DEFAULT_BOX_SIZE
})
const normalizedBoxDuration = computed(() => {
  const duration = Number(boxDurationSeconds.value)
  return Number.isFinite(duration) ? duration : DEFAULT_BOX_DURATION
})
const boxBoundsLabel = computed(() => {
  return `目标盒 ${normalizedBoxSize.value.toFixed(1)}m`
})
const boxSettingsSummary = computed(() => {
  return `${boxBoundsLabel.value} / ${selectedBoxDirection.value.label} / ${normalizedBoxDuration.value.toFixed(1)}s`
})
const obbSettingsSummary = computed(() => {
  return '视角前方 / 模型范围'
})

const RADIAL_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'speed', label: 'speed（速度）', min: 0, max: 20, step: 0.1 },
  { key: 'acceleration', label: 'acceleration（加速度）', min: 0, max: 10, step: 0.1 },
  { key: 'delay', label: 'delay（延迟）', min: 0, max: 10, step: 0.1 },
  { key: 'oscillationIntensity', label: 'oscillation（振荡强度）', min: 0, max: 1, step: 0.01 },
  { key: 'bandWidth', label: 'bandWidth（波带宽度）', min: 0.1, max: 5, step: 0.05 },
]
const RADIAL_REVEAL_COLOR_FIELDS: RevealColorField[] = [
  { key: 'dotTint', label: 'dotTint（点颜色）' },
  { key: 'waveTint', label: 'waveTint（波峰颜色）' },
]
const OBB_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'speed', label: 'speed（速度）', min: 0, max: 20, step: 0.1 },
  { key: 'acceleration', label: 'acceleration（加速度）', min: 0, max: 10, step: 0.1 },
  { key: 'delay', label: 'delay（延迟）', min: 0, max: 10, step: 0.1 },
  { key: 'oscillationIntensity', label: 'oscillation（振荡强度）', min: 0, max: 1, step: 0.01 },
  { key: 'bandWidth', label: 'bandWidth（波带宽度）', min: 0.1, max: 5, step: 0.05 },
]
const OBB_REVEAL_COLOR_FIELDS: RevealColorField[] = [
  { key: 'dotTint', label: 'dotTint（点颜色）' },
  { key: 'waveTint', label: 'waveTint（波峰颜色）' },
]
const RAIN_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'distance', label: 'distance（下落高度）', min: 0, max: 120, step: 0.5 },
  { key: 'speed', label: 'speed（速度）', min: 0, max: 20, step: 0.1 },
  { key: 'acceleration', label: 'acceleration（加速度）', min: 0, max: 10, step: 0.1 },
  { key: 'flightTime', label: 'flightTime（飞行时间）', min: 0.1, max: 8, step: 0.1 },
  { key: 'rainSize', label: 'rainSize（雨滴大小）', min: 0.001, max: 0.2, step: 0.001 },
  { key: 'rotation', label: 'rotation（旋转量）', min: 0, max: 2, step: 0.05 },
  { key: 'fallTintIntensity', label: 'fallTintIntensity（下落染色强度）', min: 0, max: 1, step: 0.01 },
  { key: 'hitDuration', label: 'hitDuration（落地高亮时长）', min: 0.1, max: 4, step: 0.05 },
]
const RAIN_REVEAL_COLOR_FIELDS: RevealColorField[] = [
  { key: 'fallTint', label: 'fallTint（下落颜色）' },
  { key: 'hitTint', label: 'hitTint（落地高亮颜色）' },
]
const GRID_REVEAL_NUMBER_FIELDS: RevealNumberField[] = [
  { key: 'blockSize', label: 'blockSize（网格大小）', min: 0.1, max: 10, step: 0.1 },
  { key: 'delay', label: 'delay（块延迟）', min: 0, max: 2, step: 0.01 },
  { key: 'duration', label: 'duration（移动时长）', min: 0.1, max: 5, step: 0.1 },
  { key: 'dotSize', label: 'dotSize（点大小）', min: 0.001, max: 0.2, step: 0.001 },
  { key: 'moveTintIntensity', label: 'moveTintIntensity（移动染色强度）', min: 0, max: 1, step: 0.01 },
  { key: 'landDuration', label: 'landDuration（落地高亮时长）', min: 0.1, max: 4, step: 0.05 },
]
const GRID_REVEAL_COLOR_FIELDS: RevealColorField[] = [
  { key: 'moveTint', label: 'moveTint（移动颜色）' },
  { key: 'landTint', label: 'landTint（落地高亮颜色）' },
]

const EFFECT_GROUPS: Array<{
  title: string
  mode: string
  effects: ShaderEffectOption[]
}> = [
    {
      title: '区域扫描预设',
      mode: '范围 / 方向 / 颜色 / 时间',
      effects: [
        {
          key: 'box-reveal',
          label: '盒体揭示',
          detail: '按参数揭示',
          create: ({ boxMin, boxMax, boxSweepMin, boxSweepMax }) =>
            new GSplatBoxShaderEffect({
              enabled: false,
              aabbMin: boxMin,
              aabbMax: boxMax,
              sweepMin: boxSweepMin,
              sweepMax: boxSweepMax,
              direction: getBoxSweepDirection(),
              duration: normalizedBoxDuration.value,
              visibleStart: false,
              visibleEnd: true,
              interval: 0.1,
              baseTint: new THREE.Color(1, 1, 1),
              edgeTint: createBoxEdgeTint(),
              tint: new THREE.Color(1, 1, 1),
            }),
        },
        {
          key: 'box-hide',
          label: '盒体隐藏',
          detail: '按参数隐藏',
          create: ({ boxMin, boxMax, boxSweepMin, boxSweepMax }) =>
            new GSplatBoxShaderEffect({
              enabled: false,
              aabbMin: boxMin,
              aabbMax: boxMax,
              sweepMin: boxSweepMin,
              sweepMax: boxSweepMax,
              direction: getBoxSweepDirection(true),
              duration: normalizedBoxDuration.value,
              visibleStart: true,
              visibleEnd: false,
              interval: 0.1,
              baseTint: new THREE.Color(1, 1, 1),
              edgeTint: createBoxEdgeTint(),
              tint: new THREE.Color(1, 1, 1),
            }),
        },
        {
          key: 'box-tint',
          label: '盒体染色',
          detail: '按参数染色',
          create: ({ boxMin, boxMax, boxSweepMin, boxSweepMax }) =>
            new GSplatBoxShaderEffect({
              enabled: false,
              aabbMin: boxMin,
              aabbMax: boxMax,
              sweepMin: boxSweepMin,
              sweepMax: boxSweepMax,
              direction: getBoxSweepDirection(),
              duration: normalizedBoxDuration.value,
              visibleStart: true,
              visibleEnd: true,
              interval: 0.05,
              baseTint: new THREE.Color(1, 1, 1),
              edgeTint: createBoxEdgeTint(),
              tint: createBoxTint(),
            }),
        },
        {
          key: 'box-untint',
          label: '盒体退色',
          detail: '按参数退色',
          create: ({ boxMin, boxMax, boxSweepMin, boxSweepMax }) =>
            new GSplatBoxShaderEffect({
              enabled: false,
              aabbMin: boxMin,
              aabbMax: boxMax,
              sweepMin: boxSweepMin,
              sweepMax: boxSweepMax,
              direction: getBoxSweepDirection(true),
              duration: normalizedBoxDuration.value,
              visibleStart: true,
              visibleEnd: true,
              interval: 0.05,
              invertTint: true,
              baseTint: new THREE.Color(1, 1, 1),
              edgeTint: createBoxEdgeTint(),
              tint: createBoxTint(),
            }),
        },
        {
          key: 'box-crop',
          label: '盒体裁切',
          detail: '保留盒内',
          create: ({ boxMin, boxMax }) =>
            new GSplatCropShaderEffect({
              enabled: false,
              aabbMin: boxMin,
              aabbMax: boxMax,
              edgeScaleFactor: 0.45,
            }),
        },
      ],
    },
    {
      title: '揭示动画类',
      mode: '时间驱动 / 径向揭示',
      effects: [
        {
          key: 'reveal-radial',
          label: '径向揭示',
          detail: '波前扩散',
          create: ({ center, sceneRadius }) =>
            new GSplatRevealRadial({
              enabled: false,
              center: [center.x, center.y, center.z],
              speed: radialRevealParams.value.speed,
              acceleration: radialRevealParams.value.acceleration,
              delay: radialRevealParams.value.delay,
              endRadius: sceneRadius,
              dotTint: createColorFromTuple(radialRevealParams.value.dotTint),
              waveTint: createColorFromTuple(radialRevealParams.value.waveTint),
              oscillationIntensity: radialRevealParams.value.oscillationIntensity,
              bandWidth: radialRevealParams.value.bandWidth,
            }),
        },
        {
          key: 'reveal-rain',
          label: '雨滴揭示',
          detail: '下落回弹',
          create: ({ center, sceneRadius }) =>
            new GSplatRevealRain({
              enabled: false,
              center: [center.x, center.y, center.z],
              distance: rainRevealParams.value.distance,
              speed: rainRevealParams.value.speed,
              acceleration: rainRevealParams.value.acceleration,
              flightTime: rainRevealParams.value.flightTime,
              rainSize: rainRevealParams.value.rainSize,
              rotation: rainRevealParams.value.rotation,
              fallTint: createColorFromTuple(rainRevealParams.value.fallTint),
              fallTintIntensity: rainRevealParams.value.fallTintIntensity,
              hitTint: createColorFromTuple(rainRevealParams.value.hitTint),
              hitDuration: rainRevealParams.value.hitDuration,
              endRadius: sceneRadius,
            }),
        },
        {
          key: 'reveal-grid',
          label: '网格爆发',
          detail: '分块展开',
          create: ({ center, sceneRadius }) =>
            new GSplatRevealGridEruption({
              enabled: false,
              center: [center.x, center.y, center.z],
              blockSize: gridRevealParams.value.blockSize,
              delay: gridRevealParams.value.delay,
              duration: gridRevealParams.value.duration,
              dotSize: gridRevealParams.value.dotSize,
              moveTint: createColorFromTuple(gridRevealParams.value.moveTint),
              moveTintIntensity: gridRevealParams.value.moveTintIntensity,
              landTint: createColorFromTuple(gridRevealParams.value.landTint),
              landDuration: gridRevealParams.value.landDuration,
              endRadius: sceneRadius,
            }),
        },
      ],
    },
  ]

const boxEffectGroup = EFFECT_GROUPS[0]
const revealEffectGroup = EFFECT_GROUPS[1]
const ADAPTIVE_OBB_EFFECT: ShaderEffectOption = {
  key: 'obb-double-wave',
  label: '视角双波揭示',
  detail: '按视角向前推进',
  create: ({ adaptiveObb }) =>
    new GSplatObbShaderEffect({
      enabled: false,
      mode: 'double-wave-reveal',
      origin: adaptiveObb.origin,
      axisX: adaptiveObb.axisX,
      axisY: adaptiveObb.axisY,
      axisZ: adaptiveObb.axisZ,
      min: adaptiveObb.min,
      max: adaptiveObb.max,
      direction: adaptiveObb.direction,
      sweepStart: adaptiveObb.sweepStart,
      sweepEnd: adaptiveObb.sweepEnd,
      speed: obbRevealParams.value.speed,
      acceleration: obbRevealParams.value.acceleration,
      delay: obbRevealParams.value.delay,
      dotTint: createColorFromTuple(obbRevealParams.value.dotTint),
      waveTint: createColorFromTuple(obbRevealParams.value.waveTint),
      oscillationIntensity: obbRevealParams.value.oscillationIntensity,
      bandWidth: obbRevealParams.value.bandWidth,
    }),
}
const REVEAL_PARAM_PANELS: Array<{
  key: RevealPanelKey
  effect: ShaderEffectOption
  numberFields: RevealNumberField[]
  colorFields: RevealColorField[]
}> = [
  {
    key: 'radial',
    effect: revealEffectGroup.effects[0],
    numberFields: RADIAL_REVEAL_NUMBER_FIELDS,
    colorFields: RADIAL_REVEAL_COLOR_FIELDS,
  },
  {
    key: 'rain',
    effect: revealEffectGroup.effects[1],
    numberFields: RAIN_REVEAL_NUMBER_FIELDS,
    colorFields: RAIN_REVEAL_COLOR_FIELDS,
  },
  {
    key: 'grid',
    effect: revealEffectGroup.effects[2],
    numberFields: GRID_REVEAL_NUMBER_FIELDS,
    colorFields: GRID_REVEAL_COLOR_FIELDS,
  },
]

const containerRef = useTemplateRef('containerRef')
const backendControlState: GsplatBackendControlState = readGsplatBackendControlState()
// 3DGS demo 可切换后端：切换写回 URL 后重建，排序/radix/pickIds 作为扩展区。
const graphicsCard = useExampleGraphicsCard({ presetKey: 'pointCloudAndGSplat' })
const requestedGraphicsBackend =
  backendControlState.backend === 'webgpu' ? ('webgpu' as const) : undefined
const requestedSortingMode = backendControlState.sorting
const requestedGpuRadixKind =
  backendControlState.radix
const requestedGpuPickIdMode =
  backendControlState.pickIds
/**
 * 双后端必须使用同一插件闭包：先前 WebGPU 分支静默裁剪成 View/Camera/Picking/Control/GSplat/Render，
 * 少了 PointCloud/SharedOcclusionDepth/Character/Measure/Clip 等主体，导致同一 route 两个后端的
 * 能力与业务逻辑不一致（违反 §8.9 的「不得靠静默删插件通过 WebGPU」约束）。这里统一走完整 preset，
 * 仅保留 3DGS 自身的 backend/sorting 配置差异。
 */
const plugins = configureGsplatPlugins(presets.pointCloudAndGSplat(), backendControlState)

const {
  engine,
  isLoading,
  modelInput,
  stats,
  init,
  loadModel,
  removeAllModels,
  autoLoad,
} = use3DGS(containerRef, {
  plugins,
  defaultModelUrl: DEFAULT_GS3D_MODEL,
  autoLoadFromUrl: true,
  graphics: getGsplatGraphicsConfig(backendControlState),
  fov: GS3D_EFFECTS_CAMERA_FOV,
  background: 'black',
})

const statsEnabled = ref(false)
const shaderEffectName = ref('未启用')
const activeEffectKey = ref<string | null>(null)
const loadError = ref('')
let statsInstance: Stats | null = null
let statsBeforeRenderOff: (() => void) | null = null
let statsAfterRenderOff: (() => void) | null = null
let activeShaderEffect: GSplatShaderEffectLike | null = null
let activeRelightingEffect: GSplatRelighting | null = null
let activeRelightingController: GSplatRelightingViewController | null = null
let activeRelightingProxyDisposables: Array<() => void> = []
let activeTreesEffect: GSplatTrees | null = null
let activeTreesAttachment: GSplatTreesAttachment | null = null
let activeWeatherController: GSplatWeatherController | null = null

const activeAdvancedEffectKey = ref<AdvancedEffectKey | null>(null)
const advancedEffectStatus = ref('未启用')
const relightingParams = ref({
  textureScale: 0.5,
  blend: 0.65,
  brightness: 2,
  background: 1,
})
const treesParams = ref({
  strength: 0.35,
  speed: 1.2,
  direction: 45,
  gustiness: 0.4,
  flutter: 0.3,
})
const weatherParams = ref({
  density: 2,
  speed: 1,
  fall: 4,
  drift: 0.5,
  opacity: 0.85,
  size: 0.05,
})

function readAdvancedEffectDiagnostics(): GS3DEffectsDiagnostics {
  const relighting = activeRelightingEffect
  const relightingController = activeRelightingController
  const trees = activeTreesEffect
  const weather = activeWeatherController
  return Object.freeze({
    backend: engine.value?.getGraphicsInfo().kind ?? null,
    requestedSortingMode,
    requestedGpuRadixKind: requestedGraphicsBackend === 'webgpu' ? requestedGpuRadixKind : null,
    gpuPickIdMode: requestedGpuPickIdMode,
    active: activeAdvancedEffectKey.value,
    status: advancedEffectStatus.value,
    relighting: relighting
      ? Object.freeze({
          enabled: relighting.enabled,
          initialized: relighting.initialized,
          revision: relighting.revision,
          targetSize: relightingController?.targetSize ?? null,
        })
      : null,
    trees: trees
      ? Object.freeze({
          enabled: trees.enabled,
          revision: trees.revision,
          sphereCount: trees.spheres.length,
          hasAttachment: activeTreesAttachment !== null,
          needsContinuousActivity: trees.needsContinuousActivity,
        })
      : null,
    weather: weather
      ? Object.freeze({
          hasHandle: weather.handle !== null,
          planCount: weather.plan.count,
          time: weather.weather.parameters.uWeatherTime,
          needsContinuousActivity: weather.needsContinuousActivity,
          lastError: weather.lastError?.message ?? null,
        })
      : null,
  })
}

function publishAdvancedEffectDiagnostics(): void {
  Object.defineProperty(window, '__SHARE3D_GS3D_EFFECT_DIAGNOSTICS__', {
    configurable: true,
    get: readAdvancedEffectDiagnostics,
  })
}

const modelLoaderHint = computed(() => {
  if (loadError.value) {
    return `加载失败：${loadError.value}`
  }

  return '请选择包含 lod-meta.json 的本地目录，然后点击“加载模型”。'
})

const panelMeta = computed(() => {
  return `${stats.value.totalModels} 个模型 / ${shaderEffectName.value} / ${advancedEffectStatus.value}`
})

function enableStats() {
  const engineInstance = engine.value
  const container = containerRef.value
  if (!engineInstance || !container || statsInstance) { return }

  const instance = new Stats()
  instance.showPanel(0)
  const dom = instance.dom
  dom.style.position = 'fixed'
  dom.style.left = '16px'
  dom.style.top = '16px'
  dom.style.right = 'auto'
  dom.style.zIndex = '1100'
  document.body.appendChild(dom)
  statsInstance = instance

  const onBegin = () => { instance.begin() }
  const onEnd = () => { instance.end() }
  engineInstance.events.on('frame.begin', onBegin)
  engineInstance.events.on('frame.end', onEnd)
  statsBeforeRenderOff = () => engineInstance.events.off('frame.begin', onBegin)
  statsAfterRenderOff = () => engineInstance.events.off('frame.end', onEnd)
}

function disableStats() {
  statsBeforeRenderOff?.()
  statsAfterRenderOff?.()
  statsBeforeRenderOff = null
  statsAfterRenderOff = null
  if (statsInstance) {
    statsInstance.dom.remove()
    statsInstance = null
  }
}

async function handleLoadModel() {
  loadError.value = ''

  try {
    if (stats.value.totalModels > 0) {
      clearSceneModels()
    }

    await loadModel()
    await waitForEngineFrames()
    setInitialLoadedCameraView()
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '未知错误'
  }
}

function clearSceneModels() {
  disposeAdvancedEffect(false)
  clearShaderEffect(false)

  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')

  for (const handle of plugin?.getAll() ?? []) {
    plugin?.unmount(handle.id)
    plugin?.dispose(handle.id)
  }

  removeAllModels(false)
  loadError.value = ''
}

function disposeAdvancedEffect(syncState = true) {
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')

  if (activeRelightingController) {
    if (!plugin?.disposeRelightingViewController(activeRelightingController)) {
      activeRelightingController.destroy()
    }
    activeRelightingController = null
  }
  activeRelightingEffect?.destroy()
  activeRelightingEffect = null
  disposeRelightingProxyDisposables()

  activeTreesAttachment?.dispose()
  activeTreesAttachment = null
  activeTreesEffect?.destroy()
  activeTreesEffect = null

  activeWeatherController?.dispose()
  activeWeatherController = null

  activeAdvancedEffectKey.value = null
  advancedEffectStatus.value = '未启用'
  publishAdvancedEffectDiagnostics()
  if (syncState) {
    loadError.value = ''
  }
}

function disposeRelightingProxyDisposables() {
  for (const dispose of activeRelightingProxyDisposables.splice(0)) {
    dispose()
  }
}

function getFirstGSplatHandle() {
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')
  return plugin?.getAll()[0] ?? null
}

function setActiveShaderEffect(effect: GSplatShaderEffectLike | null, option: ShaderEffectOption | null) {
  activeShaderEffect = effect
  shaderEffectName.value = option?.label ?? '未启用'
  activeEffectKey.value = option?.key ?? null
}

function getBoxSweepDirection(reverse = false): Vector3Tuple {
  const [x, y, z] = selectedBoxDirection.value.vector
  const sign = reverse ? -1 : 1
  return [x * sign, y * sign, z * sign]
}

function createBoxTint(): THREE.Color {
  return new THREE.Color(boxTintColor.value)
}

function createBoxEdgeTint(): THREE.Color {
  return new THREE.Color(boxEdgeColor.value).multiplyScalar(BOX_EDGE_TINT_INTENSITY)
}

function createColorFromTuple(color: ColorTuple): THREE.Color {
  return new THREE.Color(color[0], color[1], color[2])
}

function createColorControl(color: ColorTuple): RevealColorControl {
  return {
    hex: colorTupleToHex(color),
    intensity: getColorIntensity(color) || DEFAULT_COLOR_INTENSITY,
  }
}

function getColorIntensity(color: ColorTuple): number {
  return Math.min(COLOR_INTENSITY_MAX, Math.max(0, color[0], color[1], color[2]))
}

function getColorBase(color: ColorTuple): ColorTuple {
  const intensity = getColorIntensity(color)
  if (intensity <= 0.00001) {
    return [1, 1, 1]
  }

  return [
    Math.min(1, Math.max(0, color[0] / intensity)),
    Math.min(1, Math.max(0, color[1] / intensity)),
    Math.min(1, Math.max(0, color[2] / intensity)),
  ]
}

function colorTupleToHex(color: ColorTuple): string {
  const base = getColorBase(color)
  return `#${base.map((value) => Math.round(value * 255).toString(16).padStart(2, '0')).join('')}`
}

function parseColorHex(hex: string): ColorTuple | null {
  const value = /^#?([0-9a-f]{6})$/i.exec(hex.trim())?.[1]
  if (!value) {
    return null
  }

  return [
    Number.parseInt(value.slice(0, 2), 16) / 255,
    Number.parseInt(value.slice(2, 4), 16) / 255,
    Number.parseInt(value.slice(4, 6), 16) / 255,
  ]
}

function applyColorIntensity(base: ColorTuple, intensity: number): ColorTuple {
  const value = Math.min(COLOR_INTENSITY_MAX, Math.max(0, intensity))
  return [base[0] * value, base[1] * value, base[2] * value]
}

function getRevealParams(panelKey: RevealPanelKey): Record<string, unknown> {
  if (panelKey === 'radial') {
    return radialRevealParams.value
  }
  if (panelKey === 'rain') {
    return rainRevealParams.value
  }
  return gridRevealParams.value
}

function getRevealNumberParam(panelKey: RevealPanelKey, paramKey: string): number {
  const value = getRevealParams(panelKey)[paramKey]
  return typeof value === 'number' ? value : 0
}

function setRevealNumberParam(panelKey: RevealPanelKey, field: RevealNumberField, event: Event, effectKey: string) {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  const value = Math.min(field.max, Math.max(field.min, rawValue))
  getRevealParams(panelKey)[field.key] = value
  refreshActiveRevealEffect(effectKey)
}

function getRevealColorParam(panelKey: RevealPanelKey, paramKey: string): ColorTuple {
  const value = getRevealParams(panelKey)[paramKey]
  return Array.isArray(value) && value.length === 3 ? value as ColorTuple : [0, 0, 0]
}

function getRevealColorControl(panelKey: RevealPanelKey, paramKey: string): RevealColorControl {
  const controls = revealColorControls.value[panelKey]
  if (!controls[paramKey]) {
    controls[paramKey] = createColorControl(getRevealColorParam(panelKey, paramKey))
  }
  return controls[paramKey]
}

function getRevealColorHex(panelKey: RevealPanelKey, paramKey: string): string {
  return getRevealColorControl(panelKey, paramKey).hex
}

function getRevealColorIntensity(panelKey: RevealPanelKey, paramKey: string): number {
  return getRevealColorControl(panelKey, paramKey).intensity
}

function syncRevealColorParam(panelKey: RevealPanelKey, paramKey: string) {
  const control = getRevealColorControl(panelKey, paramKey)
  const base = parseColorHex(control.hex)
  if (!base) {
    return
  }
  getRevealParams(panelKey)[paramKey] = applyColorIntensity(base, control.intensity)
}

function setRevealColorHex(panelKey: RevealPanelKey, paramKey: string, event: Event, effectKey: string) {
  const input = event.target as HTMLInputElement
  if (!parseColorHex(input.value)) {
    return
  }

  getRevealColorControl(panelKey, paramKey).hex = input.value
  syncRevealColorParam(panelKey, paramKey)
  refreshActiveRevealEffect(effectKey)
}

function setRevealColorIntensity(panelKey: RevealPanelKey, paramKey: string, event: Event, effectKey: string) {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  getRevealColorControl(panelKey, paramKey).intensity = Math.min(COLOR_INTENSITY_MAX, Math.max(0, rawValue))
  syncRevealColorParam(panelKey, paramKey)
  refreshActiveRevealEffect(effectKey)
}

function getObbRevealNumberParam(paramKey: string): number {
  const value = obbRevealParams.value[paramKey as keyof typeof obbRevealParams.value]
  return typeof value === 'number' ? value : 0
}

function setObbRevealNumberParam(field: RevealNumberField, event: Event) {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  obbRevealParams.value[field.key as keyof typeof obbRevealParams.value] = Math.min(field.max, Math.max(field.min, rawValue)) as never
  refreshActiveObbEffect()
}

function getObbRevealColorParam(paramKey: string): ColorTuple {
  const value = obbRevealParams.value[paramKey as keyof typeof obbRevealParams.value]
  return Array.isArray(value) && value.length === 3 ? value as ColorTuple : [0, 0, 0]
}

function getObbRevealColorControl(paramKey: string): RevealColorControl {
  if (!obbRevealColorControls.value[paramKey]) {
    obbRevealColorControls.value[paramKey] = createColorControl(getObbRevealColorParam(paramKey))
  }
  return obbRevealColorControls.value[paramKey]
}

function getObbRevealColorHex(paramKey: string): string {
  return getObbRevealColorControl(paramKey).hex
}

function getObbRevealColorIntensity(paramKey: string): number {
  return getObbRevealColorControl(paramKey).intensity
}

function syncObbRevealColorParam(paramKey: string) {
  const control = getObbRevealColorControl(paramKey)
  const base = parseColorHex(control.hex)
  if (!base) {
    return
  }
  obbRevealParams.value[paramKey as keyof typeof obbRevealParams.value] = applyColorIntensity(base, control.intensity) as never
}

function setObbRevealColorHex(paramKey: string, event: Event) {
  const input = event.target as HTMLInputElement
  if (!parseColorHex(input.value)) {
    return
  }

  getObbRevealColorControl(paramKey).hex = input.value
  syncObbRevealColorParam(paramKey)
  refreshActiveObbEffect()
}

function setObbRevealColorIntensity(paramKey: string, event: Event) {
  const input = event.target as HTMLInputElement
  const rawValue = Number(input.value)
  if (!Number.isFinite(rawValue)) {
    return
  }

  getObbRevealColorControl(paramKey).intensity = Math.min(COLOR_INTENSITY_MAX, Math.max(0, rawValue))
  syncObbRevealColorParam(paramKey)
  refreshActiveObbEffect()
}

function setBoxDirection(key: BoxSweepDirectionKey) {
  boxDirectionKey.value = key
  refreshActiveBoxEffect()
}

function setBoxTintColor(color: string) {
  boxTintColor.value = color
  refreshActiveBoxEffect()
}

function setBoxEdgeColor(color: string) {
  boxEdgeColor.value = color
  refreshActiveBoxEffect()
}

function normalizeBoxSize() {
  const size = Number(boxSize.value)
  boxSize.value = Number.isFinite(size) ? Math.min(1000, Math.max(0.5, size)) : DEFAULT_BOX_SIZE
  refreshActiveBoxEffect()
}

function normalizeBoxDuration() {
  const duration = Number(boxDurationSeconds.value)
  boxDurationSeconds.value = Number.isFinite(duration) ? Math.min(12, Math.max(0.5, duration)) : DEFAULT_BOX_DURATION
  refreshActiveBoxEffect()
}

function refreshActiveBoxEffect() {
  if (!activeEffectKey.value?.startsWith('box-')) {
    return
  }

  const activeOption = findEffectOption(activeEffectKey.value)
  if (activeOption && stats.value.totalModels > 0) {
    applyShaderEffectOption(activeOption)
  }
}

function refreshActiveRevealEffect(effectKey: string) {
  if (activeEffectKey.value !== effectKey) {
    return
  }

  const activeOption = findEffectOption(activeEffectKey.value)
  if (activeOption && stats.value.totalModels > 0) {
    applyShaderEffectOption(activeOption)
  }
}

function refreshActiveObbEffect() {
  if (activeEffectKey.value === ADAPTIVE_OBB_EFFECT.key && stats.value.totalModels > 0) {
    applyShaderEffectOption(ADAPTIVE_OBB_EFFECT)
  }
}

function findEffectOption(key: string): ShaderEffectOption | null {
  if (key === ADAPTIVE_OBB_EFFECT.key) {
    return ADAPTIVE_OBB_EFFECT
  }

  for (const group of EFFECT_GROUPS) {
    const option = group.effects.find((effect) => effect.key === key)
    if (option) {
      return option
    }
  }

  return null
}

function createEffectRuntimeContext(): EffectRuntimeContext {
  const center = engine.value?.camera.getState().target.clone() ?? new THREE.Vector3()
  const sceneBox = getSceneBounds()
  const halfBoxSize = normalizedBoxSize.value * 0.5
  const targetBox = new THREE.Box3(
    new THREE.Vector3(center.x - halfBoxSize, center.y - halfBoxSize, center.z - halfBoxSize),
    new THREE.Vector3(center.x + halfBoxSize, center.y + halfBoxSize, center.z + halfBoxSize)
  )
  const box = targetBox
  const sweepBox = getEffectiveBoxSweepBounds(box, getFirstGSplatWorldBounds() ?? sceneBox)

  return {
    center,
    boxMin: toVectorTuple(box.min),
    boxMax: toVectorTuple(box.max),
    boxSweepMin: toVectorTuple(sweepBox.min),
    boxSweepMax: toVectorTuple(sweepBox.max),
    adaptiveObb: createAdaptiveObbParameters(),
    sceneRadius: getSceneRadius(center, sceneBox),
  }
}

function createAdaptiveObbParameters(): CameraAdaptiveObbParameters {
  const engineInstance = engine.value
  const cameraState = engineInstance?.camera.getState()
  const worldBounds = getFirstGSplatWorldBounds() ?? getSceneBounds()
  const cameraPosition = cameraState?.position.clone() ?? new THREE.Vector3(0, 0, 0)
  const cameraTarget = cameraState?.target.clone() ?? new THREE.Vector3(0, 1, 0)
  const up = cameraState?.up.clone() ?? new THREE.Vector3(0, 0, 1)

  return createCameraAdaptiveObb({
    cameraPosition,
    cameraTarget,
    up,
    bounds: worldBounds,
  })
}

function getEffectiveBoxSweepBounds(box: THREE.Box3, sceneBox: THREE.Box3 | null): THREE.Box3 {
  if (!sceneBox || sceneBox.isEmpty()) {
    return box.clone()
  }

  const sweepBox = box.clone().intersect(sceneBox)
  return sweepBox.isEmpty() ? box.clone() : sweepBox
}

function getSceneBounds(): THREE.Box3 | null {
  const box = engine.value?.context.sceneBounds.getWorldBoundingBox({
    purpose: 'fit',
    includeInvisible: true,
  })

  if (!box || box.isEmpty()) {
    return null
  }

  return box.clone()
}

function getFirstGSplatWorldBounds(): THREE.Box3 | null {
  const handle = getFirstGSplatHandle()
  if (!handle?.boundingBox) {
    return null
  }

  const box = handle.boundingBox.clone()
  if (box.isEmpty()) {
    return null
  }

  const object = handle.object3D
  if (object) {
    object.updateWorldMatrix(true, false)
    box.applyMatrix4(object.matrixWorld)
  }

  return box.isEmpty() ? null : box
}

function getFirstGSplatLocalBounds(): THREE.Box3 | null {
  const handle = getFirstGSplatHandle()
  if (!handle?.boundingBox) {
    return null
  }

  const box = handle.boundingBox.clone()
  return box.isEmpty() ? null : box
}

function getSceneRadius(center: THREE.Vector3, box: THREE.Box3 | null): number {
  if (!box) {
    return DEFAULT_SCENE_RADIUS
  }

  const sphere = box.getBoundingSphere(new THREE.Sphere())
  return Math.max(MIN_REVEAL_RADIUS, center.distanceTo(sphere.center) + sphere.radius)
}

function toVectorTuple(vector: THREE.Vector3): [number, number, number] {
  return [vector.x, vector.y, vector.z]
}

function applyShaderEffectOption(option: ShaderEffectOption) {
  const handle = getFirstGSplatHandle()
  if (!handle) {
    loadError.value = '请先加载 3DGS 模型'
    return
  }

  const effect = option.create(createEffectRuntimeContext())
  disposeAdvancedEffect(false)
  clearShaderEffect(false)
  if (!handle.setShaderEffect(effect)) {
    effect.destroy()
    loadError.value = '当前模型不支持着色器特效'
    return
  }

  loadError.value = ''
  setActiveShaderEffect(effect, option)
}

function restartShaderEffect() {
  if (!getFirstGSplatHandle()?.restartShaderEffect()) {
    activeShaderEffect = null
    shaderEffectName.value = '未启用'
    activeEffectKey.value = null
  }
}

function clearShaderEffect(syncState = true) {
  disposeAdvancedEffect(false)
  getFirstGSplatHandle()?.clearShaderEffect()
  activeShaderEffect?.destroy()
  activeShaderEffect = null
  shaderEffectName.value = '未启用'
  activeEffectKey.value = null
  if (syncState) {
    loadError.value = ''
  }
}

function createPrimaryRelightingFrame(): GSplatRelightingViewFrame | null {
  const engineInstance = engine.value
  if (!engineInstance) {
    return null
  }

  const viewId = engineInstance.views.primaryViewId
  const view = engineInstance.views.get(viewId)?.getSnapshot()
  const surface = engineInstance.context.graphics.surface
  const camera = engineInstance.views.requireViewportCamera(viewId).getActiveCamera()
  const surfaceWidth = Math.max(1, surface.drawingBufferWidth)
  const surfaceHeight = Math.max(1, surface.drawingBufferHeight)
  const scaleX = surface.logicalWidth > 0 ? surfaceWidth / surface.logicalWidth : 1
  const scaleY = surface.logicalHeight > 0 ? surfaceHeight / surface.logicalHeight : 1
  const rect = view?.rect ?? {
    x: 0,
    y: 0,
    width: surface.logicalWidth,
    height: surface.logicalHeight,
  }
  const x = Math.min(surfaceWidth - 1, Math.max(0, Math.round(rect.x * scaleX)))
  const y = Math.min(surfaceHeight - 1, Math.max(0, Math.round(rect.y * scaleY)))
  const width = Math.min(
    surfaceWidth - x,
    Math.max(1, Math.round(rect.width * scaleX))
  )
  const height = Math.min(
    surfaceHeight - y,
    Math.max(1, Math.round(rect.height * scaleY))
  )

  return {
    camera,
    surfaceWidth,
    surfaceHeight,
    viewRect: {
      x,
      y,
      width,
      height,
    },
  }
}

function createRelightingProxyScene(): THREE.Scene {
  const scene = new THREE.Scene()
  const bounds = getFirstGSplatWorldBounds() ?? getSceneBounds()
  const sphere = bounds?.getBoundingSphere(new THREE.Sphere()) ?? new THREE.Sphere(new THREE.Vector3(), 1)
  const radius = Math.max(1, sphere.radius)
  const center = sphere.center
  const colors = [0x4aa3ff, 0xff8a24, 0x66ffcc]
  const offsets = [
    new THREE.Vector3(-0.35, 0.2, 0.1),
    new THREE.Vector3(0.32, 0.18, 0.28),
    new THREE.Vector3(0.05, -0.38, -0.12),
  ]

  for (let index = 0; index < colors.length; index += 1) {
    const geometry = new THREE.SphereGeometry(radius * 0.16, 24, 12)
    const material = new THREE.MeshBasicMaterial({
      color: colors[index],
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    })
    const mesh = new THREE.Mesh(geometry, material)
    mesh.position.copy(center).add(offsets[index].clone().multiplyScalar(radius))
    scene.add(mesh)
    activeRelightingProxyDisposables.push(() => {
      geometry.dispose()
      material.dispose()
    })
  }

  return scene
}

function enableRelightingCard() {
  const handle = getFirstGSplatHandle()
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')
  const frame = createPrimaryRelightingFrame()
  if (!handle || !plugin || !frame) {
    loadError.value = '请先加载 3DGS 模型'
    return
  }

  disposeAdvancedEffect(false)
  clearShaderEffect(false)
  const effect = new GSplatRelighting({
    textureScale: relightingParams.value.textureScale,
    blend: relightingParams.value.blend,
    brightness: relightingParams.value.brightness,
    background: relightingParams.value.background,
  })
  if (!handle.setShaderEffect(effect)) {
    effect.destroy()
    loadError.value = '当前模型不支持 Relighting shader effect'
    return
  }

  let controller: GSplatRelightingViewController | null = null
  try {
    controller = plugin.createRelightingViewController({
      viewId: engine.value!.views.primaryViewId,
      effect,
      proxyScene: createRelightingProxyScene(),
    })
    controller.enable(frame)
  } catch (error) {
    if (controller) {
      plugin.disposeRelightingViewController(controller)
    }
    disposeRelightingProxyDisposables()
    effect.destroy()
    loadError.value = error instanceof Error ? error.message : 'Relighting 启用失败'
    return
  }

  activeRelightingEffect = effect
  activeRelightingController = controller
  activeAdvancedEffectKey.value = 'relighting'
  advancedEffectStatus.value = 'Relighting'
  loadError.value = ''
  publishAdvancedEffectDiagnostics()
}

function updateRelightingCard() {
  const effect = activeRelightingEffect
  if (!effect) {
    return
  }
  effect.textureScale = relightingParams.value.textureScale
  effect.blend = relightingParams.value.blend
  effect.brightness = relightingParams.value.brightness
  effect.background = relightingParams.value.background
  publishAdvancedEffectDiagnostics()
}

function createTreeSpheres(): GSplatTreeSphere[] {
  const bounds = getFirstGSplatLocalBounds() ?? getFirstGSplatWorldBounds()
  const sphere = bounds?.getBoundingSphere(new THREE.Sphere()) ?? new THREE.Sphere(new THREE.Vector3(), 1)
  const radius = Math.max(0.1, sphere.radius)
  const center = sphere.center
  return [
    { center: center.clone().add(new THREE.Vector3(-radius * 0.24, 0, 0)), radius: radius * 0.22 },
    { center: center.clone().add(new THREE.Vector3(radius * 0.2, radius * 0.16, 0)), radius: radius * 0.18 },
    { center: center.clone().add(new THREE.Vector3(0, -radius * 0.22, radius * 0.04)), radius: radius * 0.2 },
  ]
}

function enableTreesCard() {
  const handle = getFirstGSplatHandle()
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')
  if (!handle || !plugin) {
    loadError.value = '请先加载 3DGS 模型'
    return
  }

  disposeAdvancedEffect(false)
  clearShaderEffect(false)
  const trees = new GSplatTrees({
    strength: treesParams.value.strength,
    speed: treesParams.value.speed,
    direction: treesParams.value.direction,
    gustiness: treesParams.value.gustiness,
    flutter: treesParams.value.flutter,
    spheres: createTreeSpheres(),
  })
  try {
    activeTreesAttachment = plugin.attachTrees(handle.id, trees)
  } catch (error) {
    trees.destroy()
    loadError.value = error instanceof Error ? error.message : 'Trees 启用失败'
    return
  }

  activeTreesEffect = trees
  activeAdvancedEffectKey.value = 'trees'
  advancedEffectStatus.value = 'Trees'
  loadError.value = ''
  publishAdvancedEffectDiagnostics()
}

function updateTreesCard() {
  const trees = activeTreesEffect
  if (!trees) {
    return
  }
  trees.strength = treesParams.value.strength
  trees.speed = treesParams.value.speed
  trees.direction = treesParams.value.direction
  trees.gustiness = treesParams.value.gustiness
  trees.flutter = treesParams.value.flutter
  publishAdvancedEffectDiagnostics()
}

function refreshTreesCard() {
  activeTreesEffect?.setSpheres(createTreeSpheres())
  activeTreesAttachment?.rebuild()
  publishAdvancedEffectDiagnostics()
}

function enableWeatherCard() {
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')
  if (!plugin) {
    loadError.value = '请先初始化 3DGS 引擎'
    return
  }

  disposeAdvancedEffect(false)
  clearShaderEffect(false)
  try {
    activeWeatherController = plugin.createWeather({
      name: 'Weather 验证',
      density: weatherParams.value.density,
      speed: weatherParams.value.speed,
      fall: weatherParams.value.fall,
      drift: weatherParams.value.drift,
      opacity: weatherParams.value.opacity,
      size: weatherParams.value.size,
      basePosition: engine.value?.camera.getState().target.clone(),
    })
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Weather 启用失败'
    return
  }

  activeAdvancedEffectKey.value = 'weather'
  advancedEffectStatus.value = 'Weather'
  loadError.value = ''
  publishAdvancedEffectDiagnostics()
}

function updateWeatherCard() {
  if (!activeWeatherController) {
    return
  }
  activeWeatherController.rebuild({
    density: weatherParams.value.density,
    speed: weatherParams.value.speed,
    fall: weatherParams.value.fall,
    drift: weatherParams.value.drift,
    opacity: weatherParams.value.opacity,
    size: weatherParams.value.size,
    basePosition: engine.value?.camera.getState().target.clone(),
  })
  publishAdvancedEffectDiagnostics()
}

function setInitialLoadedCameraView() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }
  applyGs3dEffectsInitialCamera(engineInstance)
}

function waitForEngineFrames(frameCount = 2, timeoutMs = 500): Promise<void> {
  const engineInstance = engine.value
  if (!engineInstance || frameCount <= 0) {
    return Promise.resolve()
  }
  const events = engineInstance.events

  return new Promise((resolve) => {
    let remainingFrames = frameCount
    let settled = false
    const timeoutId = window.setTimeout(cleanup, timeoutMs)

    function cleanup() {
      if (settled) {
        return
      }
      settled = true
      window.clearTimeout(timeoutId)
      events.off('frame.end', handleFrameEnd)
      resolve()
    }

    function handleFrameEnd() {
      remainingFrames -= 1
      if (remainingFrames <= 0) {
        cleanup()
      }
    }

    events.on('frame.end', handleFrameEnd)
  })
}

watch(statsEnabled, (next) => {
  if (next) {
    enableStats()
  } else {
    disableStats()
  }
})

onMounted(async () => {
  publishAdvancedEffectDiagnostics()
  graphicsCard.markCreating()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(engine.value ? engine.value.getGraphicsInfo().kind : null)
  engine.value?.controls.setMode('earth')
  if (!modelInput.value) {
    modelInput.value = DEFAULT_GS3D_MODEL
  }
  await autoLoad()
  if (stats.value.totalModels > 0) {
    await waitForEngineFrames()
    setInitialLoadedCameraView()
  }
  if (statsEnabled.value) {
    enableStats()
  }
  publishAdvancedEffectDiagnostics()
})

onBeforeUnmount(() => {
  clearShaderEffect(false)
  disableStats()
  Reflect.deleteProperty(window, '__SHARE3D_GS3D_EFFECT_DIAGNOSTICS__')
})
</script>

<template>
  <div class="gs3d-page">
    <div class="render-container" ref="containerRef"></div>

    <button class="stats-toggle" :class="{ active: statsEnabled }" type="button" @click="statsEnabled = !statsEnabled">
      {{ statsEnabled ? '隐藏 FPS' : '显示 FPS' }}
    </button>

    <SceneControlPanel
      layout="sidebar"
      kicker="示例面板"
      title="3DGS 特效"
      :meta="panelMeta"
    >
      <SceneAssetLoaderCard v-model="modelInput" badge="高斯场景" :hint="modelLoaderHint" :loading="isLoading"
        :can-clear="stats.totalModels > 0" :directory-entry-names="['lod-meta.json']"
        :preset-models="PRESET_MODELS" @submit-load="handleLoadModel()" @clear-scene="clearSceneModels()" />

      <GraphicsBackendControlCard :card="graphicsCard">
        <template #extensions>
          <GSplatBackendControlCard :state="backendControlState" />
        </template>
      </GraphicsBackendControlCard>

      <SceneGroupCard title="区域扫描特效" :subtitle="boxEffectGroup.mode" :badge="boxSettingsSummary">
        <div class="effect-groups">
          <section class="box-controls">
            <div class="effect-group-header">
              <span class="effect-category">盒体参数</span>
              <span class="effect-mode">{{ boxSettingsSummary }}</span>
            </div>

            <label class="box-field">
              <span class="box-label">目标盒边长</span>
              <div class="duration-row">
                <input
                  v-model.number="boxSize"
                  type="range"
                  min="0.5"
                  max="1000"
                  step="0.5"
                  class="duration-slider"
                  @change="normalizeBoxSize"
                />
                <input
                  v-model.number="boxSize"
                  type="number"
                  min="0.5"
                  max="1000"
                  step="0.5"
                  class="duration-input"
                  @change="normalizeBoxSize"
                />
              </div>
            </label>

            <div class="box-field">
              <span class="box-label">方向</span>
              <div class="direction-actions">
                <button
                  v-for="direction in BOX_SWEEP_DIRECTIONS"
                  :key="direction.key"
                  type="button"
                  class="direction-button"
                  :class="{ active: boxDirectionKey === direction.key }"
                  @click="setBoxDirection(direction.key)"
                >
                  {{ direction.label }}
                </button>
              </div>
            </div>

            <label class="box-field">
              <span class="box-label">时间</span>
              <div class="duration-row">
                <input
                  v-model.number="boxDurationSeconds"
                  type="range"
                  min="0.5"
                  max="12"
                  step="0.25"
                  class="duration-slider"
                  @change="normalizeBoxDuration"
                />
                <input
                  v-model.number="boxDurationSeconds"
                  type="number"
                  min="0.5"
                  max="12"
                  step="0.25"
                  class="duration-input"
                  @change="normalizeBoxDuration"
                />
              </div>
            </label>

            <div class="box-color-grid">
              <div class="box-field">
                <span class="box-label">染色</span>
                <div class="color-row">
                  <input v-model="boxTintColor" type="color" class="color-input" @change="refreshActiveBoxEffect" />
                  <button
                    v-for="preset in BOX_COLOR_PRESETS"
                    :key="`tint-${preset.value}`"
                    type="button"
                    class="color-swatch"
                    :class="{ active: boxTintColor === preset.value }"
                    :style="{ backgroundColor: preset.value }"
                    :title="preset.label"
                    @click="setBoxTintColor(preset.value)"
                  ></button>
                </div>
              </div>

              <div class="box-field">
                <span class="box-label">边缘</span>
                <div class="color-row">
                  <input v-model="boxEdgeColor" type="color" class="color-input" @change="refreshActiveBoxEffect" />
                  <button
                    v-for="preset in BOX_COLOR_PRESETS"
                    :key="`edge-${preset.value}`"
                    type="button"
                    class="color-swatch"
                    :class="{ active: boxEdgeColor === preset.value }"
                    :style="{ backgroundColor: preset.value }"
                    :title="preset.label"
                    @click="setBoxEdgeColor(preset.value)"
                  ></button>
                </div>
              </div>
            </div>
          </section>

          <section class="effect-group">
            <div class="effect-group-header">
              <span class="effect-category">{{ boxEffectGroup.title }}</span>
              <span class="effect-mode">{{ boxEffectGroup.mode }}</span>
            </div>

            <div class="effect-actions">
              <button
                v-for="effect in boxEffectGroup.effects"
                :key="effect.key"
                type="button"
                class="effect-button"
                :class="{ active: activeEffectKey === effect.key }"
                :disabled="stats.totalModels === 0"
                @click="applyShaderEffectOption(effect)"
              >
                <span class="effect-name">{{ effect.label }}</span>
                <span class="effect-detail">{{ effect.detail }}</span>
              </button>
            </div>
          </section>
        </div>
      </SceneGroupCard>

      <SceneGroupCard title="显现动画" :subtitle="revealEffectGroup.mode" badge="PlayCanvas 默认">
        <div class="effect-groups">
          <section v-for="panel in REVEAL_PARAM_PANELS" :key="panel.key" class="reveal-param-panel">
            <div class="reveal-panel-header">
              <div class="reveal-panel-title">
                <span class="effect-category">{{ panel.effect.label }}</span>
                <span class="effect-mode">{{ panel.effect.detail }}</span>
              </div>

              <button
                type="button"
                class="effect-button reveal-apply-button"
                :class="{ active: activeEffectKey === panel.effect.key }"
                :disabled="stats.totalModels === 0"
                @click="applyShaderEffectOption(panel.effect)"
              >
                启用
              </button>
            </div>

            <div class="reveal-param-grid">
              <label v-for="field in panel.numberFields" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="duration-row">
                  <input
                    :value="getRevealNumberParam(panel.key, field.key)"
                    type="range"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-slider"
                    @input="setRevealNumberParam(panel.key, field, $event, panel.effect.key)"
                  />
                  <input
                    :value="getRevealNumberParam(panel.key, field.key)"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-input"
                    @change="setRevealNumberParam(panel.key, field, $event, panel.effect.key)"
                  />
                </div>
              </label>
            </div>

            <div class="reveal-color-grid">
              <div v-for="field in panel.colorFields" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="color-control-row">
                  <input
                    :value="getRevealColorHex(panel.key, field.key)"
                    type="color"
                    class="color-input"
                    @input="setRevealColorHex(panel.key, field.key, $event, panel.effect.key)"
                  />
                  <label class="color-intensity-row">
                    <span class="color-intensity-label">强度</span>
                    <input
                      :value="getRevealColorIntensity(panel.key, field.key)"
                      type="range"
                      min="0"
                      :max="COLOR_INTENSITY_MAX"
                      :step="COLOR_INTENSITY_STEP"
                      class="duration-slider"
                      @input="setRevealColorIntensity(panel.key, field.key, $event, panel.effect.key)"
                    />
                    <input
                      :value="getRevealColorIntensity(panel.key, field.key)"
                      type="number"
                      min="0"
                      :max="COLOR_INTENSITY_MAX"
                      :step="COLOR_INTENSITY_STEP"
                      class="duration-input"
                      @change="setRevealColorIntensity(panel.key, field.key, $event, panel.effect.key)"
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>
        </div>
      </SceneGroupCard>

      <SceneGroupCard title="视角推进揭示" subtitle="从当前视角向前推进" :badge="obbSettingsSummary">
        <div class="effect-groups">
          <section class="reveal-param-panel">
            <div class="reveal-panel-header">
              <div class="reveal-panel-title">
                <span class="effect-category">{{ ADAPTIVE_OBB_EFFECT.label }}</span>
                <span class="effect-mode">{{ ADAPTIVE_OBB_EFFECT.detail }}</span>
              </div>

              <button
                type="button"
                class="effect-button reveal-apply-button"
                :class="{ active: activeEffectKey === ADAPTIVE_OBB_EFFECT.key }"
                :disabled="stats.totalModels === 0"
                @click="applyShaderEffectOption(ADAPTIVE_OBB_EFFECT)"
              >
                启用
              </button>
            </div>

            <div class="reveal-param-grid">
              <label v-for="field in OBB_REVEAL_NUMBER_FIELDS" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="duration-row">
                  <input
                    :value="getObbRevealNumberParam(field.key)"
                    type="range"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-slider"
                    @input="setObbRevealNumberParam(field, $event)"
                  />
                  <input
                    :value="getObbRevealNumberParam(field.key)"
                    type="number"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step"
                    class="duration-input"
                    @change="setObbRevealNumberParam(field, $event)"
                  />
                </div>
              </label>
            </div>

            <div class="reveal-color-grid">
              <div v-for="field in OBB_REVEAL_COLOR_FIELDS" :key="field.key" class="box-field">
                <span class="box-label">{{ field.label }}</span>
                <div class="color-control-row">
                  <input
                    :value="getObbRevealColorHex(field.key)"
                    type="color"
                    class="color-input"
                    @input="setObbRevealColorHex(field.key, $event)"
                  />
                  <label class="color-intensity-row">
                    <span class="color-intensity-label">强度</span>
                    <input
                      :value="getObbRevealColorIntensity(field.key)"
                      type="range"
                      min="0"
                      :max="COLOR_INTENSITY_MAX"
                      :step="COLOR_INTENSITY_STEP"
                      class="duration-slider"
                      @input="setObbRevealColorIntensity(field.key, $event)"
                    />
                    <input
                      :value="getObbRevealColorIntensity(field.key)"
                      type="number"
                      min="0"
                      :max="COLOR_INTENSITY_MAX"
                      :step="COLOR_INTENSITY_STEP"
                      class="duration-input"
                      @change="setObbRevealColorIntensity(field.key, $event)"
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>
        </div>
      </SceneGroupCard>

      <SceneGroupCard title="高级 3DGS 能力" subtitle="Relighting / Trees / Weather" :badge="advancedEffectStatus">
        <div class="advanced-effect-grid">
          <section class="advanced-effect-panel">
            <div class="effect-group-header">
              <span class="effect-category">Relighting</span>
              <span class="effect-mode">每 View 私有 lighting target</span>
            </div>
            <p class="advanced-effect-description">
              用公开 Plugin façade 创建当前主视口的私有 target，并以代理几何体观察采样调制。
            </p>
            <label class="box-field">
              <span class="box-label">target 缩放</span>
              <input v-model.number="relightingParams.textureScale" type="range" min="0.1" max="1" step="0.1" @input="updateRelightingCard" />
            </label>
            <label class="box-field">
              <span class="box-label">blend / brightness</span>
              <div class="advanced-number-row">
                <input v-model.number="relightingParams.blend" type="number" min="0" max="1" step="0.05" @change="updateRelightingCard" />
                <input v-model.number="relightingParams.brightness" type="number" min="0" max="5" step="0.1" @change="updateRelightingCard" />
              </div>
            </label>
            <button
              type="button"
              class="effect-button"
              :class="{ active: activeAdvancedEffectKey === 'relighting' }"
              :disabled="stats.totalModels === 0"
              @click="enableRelightingCard"
            >
              启用 Relighting
            </button>
          </section>

          <section class="advanced-effect-panel">
            <div class="effect-group-header">
              <span class="effect-category">Trees</span>
              <span class="effect-mode">Unified WorkBuffer / GPU pick 同源</span>
            </div>
            <p class="advanced-effect-description">
              在当前模型上绑定确定性的树冠球集合；参数变化只更新 effect，显式刷新才重建 tree stream。
            </p>
            <label class="box-field">
              <span class="box-label">strength / speed</span>
              <div class="advanced-number-row">
                <input v-model.number="treesParams.strength" type="number" min="0" max="3" step="0.05" @change="updateTreesCard" />
                <input v-model.number="treesParams.speed" type="number" min="0" max="10" step="0.1" @change="updateTreesCard" />
              </div>
            </label>
            <label class="box-field">
              <span class="box-label">direction / gustiness</span>
              <div class="advanced-number-row">
                <input v-model.number="treesParams.direction" type="number" min="-360" max="360" step="5" @change="updateTreesCard" />
                <input v-model.number="treesParams.gustiness" type="number" min="0" max="3" step="0.05" @change="updateTreesCard" />
              </div>
            </label>
            <div class="advanced-button-row">
              <button
                type="button"
                class="effect-button"
                :class="{ active: activeAdvancedEffectKey === 'trees' }"
                :disabled="stats.totalModels === 0"
                @click="enableTreesCard"
              >
                启用 Trees
              </button>
              <button type="button" class="effect-button subtle" :disabled="activeAdvancedEffectKey !== 'trees'" @click="refreshTreesCard">
                LOD Rebake
              </button>
            </div>
          </section>

          <section class="advanced-effect-panel">
            <div class="effect-group-header">
              <span class="effect-category">Weather</span>
              <span class="effect-mode">过程式 Resource / Placement</span>
            </div>
            <p class="advanced-effect-description">
              创建 System-owned 的过程式高斯天气；WebGPU 使用 animated center，CPU/WebGL 保留静态中心近似。
            </p>
            <label class="box-field">
              <span class="box-label">density / speed</span>
              <div class="advanced-number-row">
                <input v-model.number="weatherParams.density" type="number" min="0.1" max="8" step="0.1" @change="updateWeatherCard" />
                <input v-model.number="weatherParams.speed" type="number" min="0" max="10" step="0.1" @change="updateWeatherCard" />
              </div>
            </label>
            <label class="box-field">
              <span class="box-label">fall / opacity</span>
              <div class="advanced-number-row">
                <input v-model.number="weatherParams.fall" type="number" min="0" max="20" step="0.5" @change="updateWeatherCard" />
                <input v-model.number="weatherParams.opacity" type="number" min="0" max="1" step="0.05" @change="updateWeatherCard" />
              </div>
            </label>
            <button
              type="button"
              class="effect-button"
              :class="{ active: activeAdvancedEffectKey === 'weather' }"
              @click="enableWeatherCard"
            >
              启用 Weather
            </button>
          </section>
        </div>
        <div class="advanced-effect-footer">
          <span>当前：{{ advancedEffectStatus }}</span>
          <button type="button" class="effect-button subtle" :disabled="!activeAdvancedEffectKey" @click="disposeAdvancedEffect()">
            停用高级能力
          </button>
        </div>
      </SceneGroupCard>

      <div class="effect-toolbar">
        <button type="button" class="effect-button subtle" :disabled="shaderEffectName === '未启用'" @click="restartShaderEffect">
          重启
        </button>
        <button type="button" class="effect-button subtle" :disabled="shaderEffectName === '未启用'" @click="clearShaderEffect()">
          清除
        </button>
      </div>
    </SceneControlPanel>
  </div>
</template>

<style scoped>
.gs3d-page {
  --panel-gap: 0px;
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(320px, 3fr);
  gap: var(--panel-gap);
  padding: var(--panel-gap);
  box-sizing: border-box;
  align-items: stretch;
}

.render-container {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
}

.stats-toggle {
  position: absolute;
  left: 32px;
  bottom: 32px;
  z-index: 1100;
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  backdrop-filter: blur(4px);
  pointer-events: auto;
}

.stats-toggle.active {
  background: rgba(64, 160, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.6);
}

.effect-groups {
  display: grid;
  gap: 14px;
}

.effect-group {
  display: grid;
  gap: 8px;
}

.reveal-param-panel {
  display: grid;
  gap: 10px;
  padding-top: 2px;
}

.reveal-param-panel + .reveal-param-panel {
  padding-top: 12px;
  border-top: 1px solid rgba(137, 205, 225, 0.18);
}

.reveal-panel-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 74px;
  gap: 10px;
  align-items: center;
}

.reveal-panel-title {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.reveal-param-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.reveal-color-grid {
  display: grid;
  gap: 10px;
}

.color-control-row {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.color-intensity-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 68px;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.color-intensity-label {
  color: rgba(217, 239, 247, 0.72);
  font-size: 11px;
}

.box-controls {
  display: grid;
  gap: 10px;
}

.box-field {
  display: grid;
  gap: 7px;
  min-width: 0;
}

.box-label {
  color: rgba(217, 239, 247, 0.76);
  font-size: 12px;
  font-weight: 600;
}

.direction-actions {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
}

.direction-button {
  min-width: 0;
  min-height: 32px;
  border: 1px solid rgba(137, 205, 225, 0.28);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  color: #f5fbff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.direction-button.active {
  border-color: rgba(110, 230, 255, 0.82);
  background: rgba(38, 150, 188, 0.46);
}

.duration-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 68px;
  gap: 8px;
  align-items: center;
}

.duration-slider {
  width: 100%;
  accent-color: #63d7f1;
}

.duration-input {
  min-width: 0;
  height: 32px;
  padding: 0 6px;
  border: 1px solid rgba(137, 205, 225, 0.28);
  border-radius: 6px;
  background: rgba(6, 10, 16, 0.52);
  color: #f5fbff;
  font-size: 12px;
}

.box-color-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.color-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.color-input {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid rgba(137, 205, 225, 0.28);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.color-swatch {
  flex: 1 1 0;
  min-width: 18px;
  height: 24px;
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 6px;
  cursor: pointer;
}

.color-swatch.active {
  border-color: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 2px rgba(110, 230, 255, 0.34);
}

.effect-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.effect-category {
  color: #f5fbff;
  font-size: 13px;
  font-weight: 700;
}

.effect-mode {
  color: rgba(210, 236, 245, 0.72);
  font-size: 12px;
}

.effect-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.effect-toolbar {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding-top: 2px;
}

.effect-button {
  min-height: 46px;
  border: 1px solid rgba(137, 205, 225, 0.35);
  border-radius: 6px;
  background: rgba(45, 123, 153, 0.26);
  color: #f5fbff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: grid;
  align-content: center;
  gap: 3px;
  padding: 7px 10px;
  text-align: left;
}

.effect-button.active {
  border-color: rgba(110, 230, 255, 0.82);
  background: rgba(38, 150, 188, 0.46);
}

.effect-button.subtle {
  min-height: 34px;
  background: rgba(255, 255, 255, 0.06);
  text-align: center;
}

.effect-button.reveal-apply-button {
  min-height: 34px;
  padding: 6px 10px;
  text-align: center;
}

.effect-name,
.effect-detail {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.effect-detail {
  color: rgba(217, 239, 247, 0.7);
  font-size: 12px;
  font-weight: 500;
}

.effect-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.advanced-effect-grid {
  display: grid;
  gap: 12px;
}

.advanced-effect-panel {
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgba(137, 205, 225, 0.18);
  border-radius: 8px;
  background: rgba(8, 18, 28, 0.38);
}

.advanced-effect-description {
  margin: 0;
  color: rgba(217, 239, 247, 0.68);
  font-size: 12px;
  line-height: 1.5;
}

.advanced-number-row,
.advanced-button-row,
.advanced-effect-footer {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.advanced-effect-footer {
  align-items: center;
  margin-top: 10px;
  color: rgba(217, 239, 247, 0.72);
  font-size: 12px;
}

.advanced-effect-footer .effect-button {
  grid-column: 2;
}

.advanced-number-row input {
  min-width: 0;
  height: 32px;
  padding: 0 8px;
  border: 1px solid rgba(137, 205, 225, 0.28);
  border-radius: 6px;
  background: rgba(6, 10, 16, 0.52);
  color: #f5fbff;
  font-size: 12px;
}
</style>
